//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import defaultTheme from './DummyPersonaEditContainer.scss';
import compose from 'recompose/compose';
import { Stack, Column, Text, Box } from '@locus-taxy/neo-ui';
import Button, {
    ButtonTypes,
    ButtonWithPopover
} from 'common/components/core/button';
import * as Immutable from 'immutable';
import { Select } from 'common/components/core/select';
import { EnhancedInput } from 'common/components/core/input';
import EntityWorkingDaysInput from 'common/entity/components/dayShift/EntityWorkingDaysInput';
import { ShiftsInfo } from 'common/models/internal/ShiftsInfo';
import HolidaySelector from 'common/entity/generic/HolidaySelector';
import { SalesCategoryGroup } from 'common/models/internal/SalesCategoryGroup';
import Switch from 'common/components/core/switch';
import NumberUtils from 'common/utils/NumberUtils';
import { PopoverPlaceEnum } from 'common/components/core/popover/popOverFactory';
import { enhancedFieldFactory } from 'common/components/core/form/EnhancedFieldFactory';
import {
    DummySalesmanProperties,
    ShiftMetadata
} from 'common/models/internal/DummySalesmanProperties';
import type { ShiftMetadataModelType } from 'common/models/flow/ShiftMetadata';
import type { DayShiftModelType } from 'common/models/flow/DayShift';
import type { PjPlanConfigurationModelType } from 'common/models/flow/PjPlanConfiguration';
import type { TFunction } from 'react-i18next';
import variables from 'common/components/core/variables.scss';

const EnhancedSelect = enhancedFieldFactory({
    FieldComponent: Select,
    attachPopover: true
});

type State = {
    editingPersona: ?Object,
    index: number,
    shiftMetadata: ShiftMetadataModelType,
    selectedSalesCategory: Array<Object>,
    capacity: number,
    defaultWorkingDays: Array<DayShiftModelType>,
    workingDays: Array<DayShiftModelType>,
    holidays: Array<Object>,
    capacityError: string,
    salesCategoryError: string,
    salesCategoriesOptions: Array<Object>
};

type Props = {
    selectedIndex: ?number,
    deleteDummyPersona: number => void,
    selectedDummyPersona: ?Object,
    onPjpConfigChange: Function,
    pjpConfiguration: PjPlanConfigurationModelType,
    closeDummyPersonaModal: Function,
    t: TFunction,
    theme: Object,
    salesCategories: Array<Object>
};

class DummyPersonaEditContainer extends React.Component<Props, State> {
    constructor(props) {
        super(props);
        const { salesmanCategoryGroups } = props.pjpConfiguration;
        const { t } = props;
        const defaultShiftsInfo = ShiftsInfo.fromJS({ workingDays: [] });
        this.state = {
            editingPersona: props.selectedDummyPersona || null,
            index:
                props.selectedIndex === null
                    ? salesmanCategoryGroups.size - 1
                    : props.selectedIndex,
            shiftMetadata: ShiftMetadata.fromJS({
                startFromFirstCustomerVisit:
                    props.selectedDummyPersona?.shiftMetadata
                        ?.startFromFirstCustomerVisit || false,
                endAtLastCustomerVisit:
                    props.selectedDummyPersona?.shiftMetadata
                        ?.endAtLastCustomerVisit || false
            }),
            selectedSalesCategory: props.selectedDummyPersona
                ? props.selectedDummyPersona.salesCategories.toJS()
                : [],
            capacity: props.selectedDummyPersona
                ? props.selectedDummyPersona.capacity
                : null,

            defaultWorkingDays: props.selectedDummyPersona
                ? props.selectedDummyPersona.workingDays
                : Immutable.List([]),
            workingDays: props.selectedDummyPersona
                ? props.selectedDummyPersona.workingDays
                : defaultShiftsInfo.initializeWeekDays(),
            holidays: props.selectedDummyPersona
                ? props.selectedDummyPersona.holidays
                : Immutable.List([]),
            salesCategoriesOptions: props.salesCategories,
            capacityError: null,
            salesCategoryError: null,
            translationsObject: {
                add: t('pjpCreate.pjpConfig.addWorkingDays'),
                change: t('pjpCreate.pjpConfig.changeWorkingDays'),
                startLocationPlaceholder: t(
                    'pjpCreate.pjpConfig.enterStartLocation'
                ),
                endLocationPlaceholder: t(
                    'pjpCreate.pjpConfig.enterEndLocation'
                )
            }
        };
    }

    validateCapacity = (capacity: number): ?string => {
        const { t } = this.props;
        if (capacity && !NumberUtils.isNumeric(capacity)) {
            return t('validations:dummyPersonaErrors.capacityNan');
        }
        if (capacity && !NumberUtils.isNonNegativeNumber(capacity)) {
            return t('validations:dummyPersonaErrors.capacityNegativeError');
        }
        return null;
    };

    getSalesCategoryError = (selectedSalesCategory: Array<string>): ?string => {
        const { t } = this.props;

        if (selectedSalesCategory && !selectedSalesCategory.length) {
            return t('validations:dummyPersonaErrors.salesCategoryEmpty');
        }
        return null;
    };

    onSalesCategoryChange = (salesCategories: Array<Object>): void => {
        this.setState({
            selectedSalesCategory: salesCategories
        });
    };

    onCapacityChange = (capacity: number): void => {
        const capacityError = this.validateCapacity(capacity);
        this.setState({ capacity, capacityError });
    };

    onWorkingDaysChange = (workingDays: Array<DayShiftModelType>): void => {
        this.setState({ workingDays });
    };

    onHolidaysChange = (holidays: Array<Object>): void => {
        this.setState({ holidays: Immutable.List(holidays) });
    };

    onStartShiftMetadataChange = (value: boolean): void => {
        const { shiftMetadata } = this.state;
        const updatedShiftMetadata = shiftMetadata.setStartFromFirstCustomerVisit(
            value
        );
        this.setState({
            shiftMetadata: updatedShiftMetadata
        });
    };

    onEndShiftMetadataChange = (value: boolean): void => {
        this.setState(({ shiftMetadata }) => {
            return {
                shiftMetadata: shiftMetadata.setEndAtLastCustomerVisit(value)
            };
        });
    };

    onDismissClick = () => {
        const {
            deleteDummyPersona,
            closeDummyPersonaModal,
            pjpConfiguration: { dummySalesmanProperties },
            selectedIndex
        } = this.props;
        if (selectedIndex === null) {
            deleteDummyPersona(dummySalesmanProperties.size - 1);
        }
        closeDummyPersonaModal();
    };

    onSaveClick = () => {
        const {
            selectedSalesCategory,
            capacity,
            holidays,
            workingDays,
            shiftMetadata,
            index
        } = this.state;
        const {
            closeDummyPersonaModal,
            pjpConfiguration,
            onPjpConfigChange
        } = this.props;

        const {
            salesmanCategoryGroups,
            salesmanCategoryGroupCapacities,
            dummySalesmanProperties
        } = pjpConfiguration;
        const salesCategoryError = this.getSalesCategoryError(
            selectedSalesCategory
        );
        const capacityError = this.validateCapacity();
        if (salesCategoryError || capacityError) {
            this.setState({
                capacityError,
                salesCategoryError
            });
            return;
        }

        const updatedDummyPeresonaInfo = {
            workingDays: workingDays.toJS(),
            holidays: holidays.toJS(),
            shiftMetadata: shiftMetadata.toJS()
        };
        const updatedSalesCategories = selectedSalesCategory.map(
            salesCategory => salesCategory.value
        );

        const updatedSalesmanCategoryGroups = salesmanCategoryGroups.set(
            index,
            SalesCategoryGroup.fromJS({ categories: updatedSalesCategories })
        );

        const updatedSalesmanGroupCapacities = salesmanCategoryGroupCapacities.set(
            index,
            capacity
        );

        const updatedDummySalesmanProperties = dummySalesmanProperties.set(
            index,
            DummySalesmanProperties.fromJS(updatedDummyPeresonaInfo)
        );

        const updatedPjpConfiguration = pjpConfiguration
            .setSalesmanCategoryGroups(updatedSalesmanCategoryGroups)
            .setSalesmanCategoryGroupCapacities(updatedSalesmanGroupCapacities)
            .setDummySalesmanProperties(updatedDummySalesmanProperties);

        onPjpConfigChange(updatedPjpConfiguration);
        closeDummyPersonaModal();
    };

    render() {
        const {
            editingPersona,
            selectedSalesCategory,
            salesCategoriesOptions,
            capacity,
            capacityError,
            workingDays,
            defaultWorkingDays,
            holidays,
            shiftMetadata,
            translationsObject
        } = this.state;
        const { theme, t } = this.props;
        return (
            <Box className={theme.dummyPersonaForm}>
                <Stack height="fluid" align="center">
                    <Column
                        className={theme.titleSection}
                        height={variables.appbarHeight}
                    >
                        <Text
                            color="white"
                            fontSize={4}
                            fontWeight="600"
                            textTransform="uppercase"
                        >
                            {editingPersona
                                ? t('pjpCreate.pjpConfig.editDummyPersona')
                                : t('pjpCreate.pjpConfig.addDummyPersona')}
                        </Text>
                    </Column>
                    <Column className={theme.contentSection}>
                        <Stack space="medium">
                            <Stack className={theme.fieldColumn}>
                                <Text
                                    className={theme.label}
                                    fontSize={1}
                                    fontWeight="600"
                                >
                                    {t('pjpCreate.pjpConfig.salesCategory')} *
                                </Text>
                                <EnhancedSelect
                                    isMulti
                                    placeholder={t(
                                        'pjpCreate.pjpConfig.pickSalesCategory'
                                    )}
                                    options={salesCategoriesOptions}
                                    error={this.getSalesCategoryError(
                                        selectedSalesCategory
                                    )}
                                    hasError={this.getSalesCategoryError(
                                        selectedSalesCategory
                                    )}
                                    isSearchable
                                    value={selectedSalesCategory || null}
                                    onChange={this.onSalesCategoryChange}
                                />
                            </Stack>
                            <Stack className={theme.fieldColumn}>
                                <Text
                                    className={theme.label}
                                    fontSize={1}
                                    fontWeight="600"
                                >
                                    {t('pjpCreate.pjpConfig.capacity')}
                                </Text>
                                <EnhancedInput
                                    theme={theme}
                                    className={theme.halfWidth}
                                    value={capacity}
                                    onChange={this.onCapacityChange}
                                    halfWidth
                                    error={capacityError}
                                    preferPlace={PopoverPlaceEnum.top}
                                />
                            </Stack>
                            <Stack className={theme.fieldColumn}>
                                <EntityWorkingDaysInput
                                    theme={theme}
                                    onChange={this.onWorkingDaysChange}
                                    translationsObject={translationsObject}
                                    defaultWorkingDays={defaultWorkingDays}
                                    workingDays={workingDays}
                                />
                            </Stack>
                            <Stack className={theme.fieldColumn}>
                                <Text
                                    className={theme.label}
                                    fontSize={1}
                                    fontWeight="600"
                                >
                                    {t('pjpCreate.pjpConfig.holidays')}
                                </Text>
                                <HolidaySelector
                                    holidays={holidays}
                                    t={t}
                                    onChange={this.onHolidaysChange}
                                />
                            </Stack>
                            <Stack space="medium">
                                <Column>
                                    <Switch
                                        theme={theme}
                                        onChange={
                                            this.onStartShiftMetadataChange
                                        }
                                        checked={
                                            shiftMetadata.startFromFirstCustomerVisit
                                        }
                                        label={t(
                                            'pjpCreate.pjpConfig.startAtFirstLocation'
                                        )}
                                    />
                                </Column>
                                <Column>
                                    <Switch
                                        onChange={this.onEndShiftMetadataChange}
                                        checked={
                                            shiftMetadata.endAtLastCustomerVisit
                                        }
                                        label={t(
                                            'pjpCreate.pjpConfig.endAtLastLocation'
                                        )}
                                    />
                                </Column>
                            </Stack>
                        </Stack>
                    </Column>
                </Stack>
                <Column className={theme.buttonsContainer}>
                    <Button
                        flat
                        semibold
                        uppercase
                        buttonType={ButtonTypes.Text}
                        label={t('dismissButton')}
                        onClick={this.onDismissClick}
                        className={theme.dismissButton}
                    />
                    <ButtonWithPopover
                        buttonType={ButtonTypes.Solid}
                        color="dark-sky-blue"
                        theme={theme}
                        smallLoader
                        onClick={this.onSaveClick}
                        label={t('pjpCreate.pjpConfig.saveDummyPersona')}
                        uppercase
                        semibold
                    />
                </Column>
            </Box>
        );
    }
}

export default compose(themr('DummyPersonaEditContainer', defaultTheme))(
    DummyPersonaEditContainer
);
